import { useEffect, useState } from "react";
import { Award, Download, Printer, Settings2 } from "lucide-react";
import { api, apiErr, openPdf } from "@/lib/api";
import { toast } from "sonner";
import { useFiscalYear } from "@/context/FiscalYearContext";
import { PageHeader, Loading, EmptyState, OfficeSelect, groupByOffice } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const SORTS = [
  { v: "date_desc", label: "修了日（新しい順）" },
  { v: "date_asc", label: "修了日（古い順）" },
  { v: "name", label: "氏名順" },
  { v: "training", label: "研修名順" },
  { v: "cert_no", label: "証明書番号順" },
];

const MONTHS_FY = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];

export default function Certificates() {
  const { fiscalYear } = useFiscalYear();
  const [certs, setCerts] = useState(null);
  const [offices, setOffices] = useState([]);
  const [officeId, setOfficeId] = useState("");
  const [printOpen, setPrintOpen] = useState(false);
  const [saveDefault, setSaveDefault] = useState(false);
  const [ps, setPs] = useState({ sort: "date_desc", date_from: "", date_to: "", issue_date: "", period_preset: "" });

  useEffect(() => {
    if (!fiscalYear) return;
    api.get(`/certificates?fiscal_year=${fiscalYear}`).then(({ data }) => setCerts(data));
  }, [fiscalYear]);
  useEffect(() => { api.get("/offices").then(({ data }) => setOffices(data)); }, []);

  const openPrintDialog = async () => {
    try {
      const { data } = await api.get("/reports/certificates/print-settings");
      setPs({ sort: data.sort || "date_desc", date_from: data.date_from || "", date_to: data.date_to || "", issue_date: data.issue_date || "", period_preset: data.period_preset || "" });
    } catch { /* use defaults */ }
    setSaveDefault(false);
    setPrintOpen(true);
  };

  const setField = (k, v) => setPs((p) => ({ ...p, [k]: v }));
  const applyPreset = (key) => {
    const pad = (n) => String(n).padStart(2, "0");
    if (key === "all") { setPs((p) => ({ ...p, date_from: "", date_to: "", period_preset: "all" })); return; }
    if (key === "year") { setPs((p) => ({ ...p, date_from: `${fiscalYear}-04-01`, date_to: `${fiscalYear + 1}-03-31`, period_preset: "year" })); return; }
    const m = parseInt(key.slice(1), 10);
    const y = m >= 4 ? fiscalYear : fiscalYear + 1;
    const last = new Date(y, m, 0).getDate();
    setPs((p) => ({ ...p, date_from: `${y}-${pad(m)}-01`, date_to: `${y}-${pad(m)}-${pad(last)}`, period_preset: key }));
  };

  const runPrint = async () => {
    if (saveDefault) {
      try { await api.put("/reports/certificates/print-settings", ps); toast.success("既定の印刷設定を保存しました"); }
      catch (e) { toast.error(apiErr(e)); }
    }
    const p = new URLSearchParams();
    p.set("fiscal_year", fiscalYear);
    if (officeId) p.set("office_id", officeId);
    if (ps.sort) p.set("sort", ps.sort);
    if (ps.date_from) p.set("date_from", ps.date_from);
    if (ps.date_to) p.set("date_to", ps.date_to);
    if (ps.issue_date) p.set("issue_date", ps.issue_date);
    setPrintOpen(false);
    openPdf(`/reports/certificates?${p.toString()}`);
  };

  if (!certs) return <Loading />;

  const Card = ({ c }) => (
    <div className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow" data-testid={`cert-${c.id}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="p-2.5 rounded-lg bg-green-50"><Award className="w-5 h-5 text-green-600" /></div>
        <span className="text-xs text-slate-400 font-mono">{c.cert_no}</span>
      </div>
      <div className="font-medium text-slate-800">{c.training_name}</div>
      <div className="text-sm text-slate-500 mt-0.5">{c.user_name}</div>
      <div className="text-xs text-slate-400 mt-1">{c.office_name || "事業所未設定"}</div>
      <div className="text-xs text-slate-400 mt-0.5">修了日：{c.completed_date}</div>
      <Button size="sm" variant="outline" className="w-full mt-3" onClick={() => openPdf(`/reports/certificate/${c.id}`)} data-testid={`cert-download-${c.id}`}>
        <Download className="w-4 h-4 mr-1" /> ダウンロード
      </Button>
    </div>
  );
  const Grid = ({ items }) => (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{items.map((c) => <Card key={c.id} c={c} />)}</div>
  );

  const filtered = officeId ? certs.filter((c) => c.office_id === officeId) : certs;
  const sections = officeId ? [] : groupByOffice(certs, offices, (c) => [c.office_id]);

  return (
    <div>
      <PageHeader title="修了証" subtitle={`${fiscalYear}年度の修了証一覧`}
        actions={
          <div className="flex gap-2 items-center">
            <OfficeSelect offices={offices} value={officeId} onChange={setOfficeId} testid="cert-office-filter" />
            <Button variant="outline" onClick={openPrintDialog} data-testid="cert-list-print"><Printer className="w-4 h-4 mr-1" />一覧印刷</Button>
          </div>
        } />
      {(officeId ? filtered.length : certs.length) ? (
        officeId ? <Grid items={filtered} /> : (
          <div className="space-y-6">
            {sections.map((sec) => (
              <div key={sec.office.id} data-testid={`cert-office-group-${sec.office.id}`}>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-sm font-semibold text-slate-700">{sec.office.name}</span>
                  <span className="text-xs text-slate-400">{sec.items.length}件</span>
                </div>
                <Grid items={sec.items} />
              </div>
            ))}
          </div>
        )
      ) : <EmptyState title="修了証はまだありません" hint="研修を修了すると自動発行されます" />}

      <Dialog open={printOpen} onOpenChange={setPrintOpen}>
        <DialogContent data-testid="cert-print-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Settings2 className="w-4 h-4" />一覧印刷の詳細設定</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <div>
              <Label className="text-slate-600">並び順</Label>
              <Select value={ps.sort} onValueChange={(v) => setField("sort", v)}>
                <SelectTrigger className="mt-1.5" data-testid="cert-print-sort"><SelectValue /></SelectTrigger>
                <SelectContent>{SORTS.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-600">対象期間（修了日）</Label>
              <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="cert-print-month-presets">
                {MONTHS_FY.map((m) => {
                  const key = `m${m}`;
                  const active = ps.period_preset === key;
                  return (
                    <button key={key} type="button" onClick={() => applyPreset(key)} data-testid={`cert-print-month-${m}`}
                      className={`px-2.5 py-1 rounded-md text-xs border transition-colors ${active ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>{m}月</button>
                  );
                })}
                <button type="button" onClick={() => applyPreset("year")} data-testid="cert-print-year"
                  className={`px-2.5 py-1 rounded-md text-xs border transition-colors ${ps.period_preset === "year" ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>通年</button>
                <button type="button" onClick={() => applyPreset("all")} data-testid="cert-print-all"
                  className={`px-2.5 py-1 rounded-md text-xs border transition-colors ${ps.period_preset === "all" ? "bg-slate-700 text-white border-slate-700" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>全期間</button>
              </div>
              <div className="flex items-center gap-2 mt-2">
                <Input type="date" value={ps.date_from} onChange={(e) => setPs((p) => ({ ...p, date_from: e.target.value, period_preset: "" }))} data-testid="cert-print-from" />
                <span className="text-slate-400">〜</span>
                <Input type="date" value={ps.date_to} onChange={(e) => setPs((p) => ({ ...p, date_to: e.target.value, period_preset: "" }))} data-testid="cert-print-to" />
              </div>
              <p className="text-xs text-slate-400 mt-1">進行年度（{fiscalYear}年度）の月をクリックで期間を指定。未指定の場合は{fiscalYear}年度が対象になります。</p>
            </div>
            <div>
              <Label className="text-slate-600">発行年月日</Label>
              <Input type="date" value={ps.issue_date} onChange={(e) => setField("issue_date", e.target.value)} className="mt-1.5" data-testid="cert-print-issue-date" />
              <p className="text-xs text-slate-400 mt-1">未指定の場合は本日の日付が印字されます。</p>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox checked={saveDefault} onCheckedChange={(v) => setSaveDefault(!!v)} data-testid="cert-print-save-default" />
              <span className="text-sm text-slate-600">この設定を既定として保存する</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPrintOpen(false)} data-testid="cert-print-cancel">キャンセル</Button>
            <Button onClick={runPrint} data-testid="cert-print-submit"><Printer className="w-4 h-4 mr-1" />PDF出力</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
